import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { type FacebookSettings, adminFetchFacebook, adminSaveFacebook } from '@/api/client';

export default function AdminFacebookSettings() {
  const { t } = useTranslation('home');
  const [form, setForm] = useState<FacebookSettings>({
    facebook_url: 'https://www.facebook.com/profile.php?id=61590138211063',
    embed_code: '',
    show_feed: 1,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);

  const fallbackUrl = 'https://www.facebook.com/profile.php?id=61590138211063';

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setUsingFallback(false);
    const data = await adminFetchFacebook();
    if (data && data.facebook_url?.trim()) {
      // Backend has saved settings — use them
      setForm({
        facebook_url: data.facebook_url,
        embed_code: data.embed_code || '',
        show_feed: data.show_feed === true || data.show_feed === 1 ? 1 : 0,
      });
    } else {
      // Backend empty or unreachable — preload defaults so admin can edit/save
      setForm({
        facebook_url: fallbackUrl,
        embed_code: '',
        show_feed: 1,
      });
      setUsingFallback(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    if (!form.facebook_url.trim()) {
      Swal.fire({
        title: t('admin_missing_url'),
        text: t('admin_enter_facebook_url'),
        icon: 'warning',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
      return;
    }
    setSaving(true);
    const res = await adminSaveFacebook({
      facebook_url: form.facebook_url,
      embed_code: form.embed_code,
      show_feed: form.show_feed,
    });
    setSaving(false);
    if (res.success) {
      Swal.fire({
        title: t('admin_settings_saved'),
        text: t('admin_api_saved'),
        icon: 'success',
        confirmButtonColor: '#3DBFA0',
        timer: 2000,
        timerProgressBar: true,
      });
      await loadSettings();
    } else {
      Swal.fire({
        title: t('admin_api_error'),
        text: res.message || '',
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-3xl">
        <div>
          <h2 className="text-xl font-bold text-brand-dark font-heading">
            {t('admin_facebook_settings')}
          </h2>
          <p className="text-sm text-brand-muted mt-1">{t('admin_facebook_desc')}</p>
        </div>
        <div className="py-12 text-center text-brand-muted text-sm">
          <i className="ri-loader-4-line animate-spin text-xl mr-2" />
          {t('admin_loading_data')}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-xl font-bold text-brand-dark font-heading">
          {t('admin_facebook_settings')}
        </h2>
        <p className="text-sm text-brand-muted mt-1">{t('admin_facebook_desc')}</p>
      </div>

      {/* Default settings banner */}
      {usingFallback && !loading && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 shrink-0">
            <i className="ri-information-line text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">
              Default Facebook settings loaded
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              These settings are displayed on the public site but are not saved in the backend yet. Save them here to enable full management.
            </p>
          </div>
          <button
            onClick={loadSettings}
            className="shrink-0 px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1" />Retry
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${form.show_feed ? 'bg-blue-50' : 'bg-gray-100'}`}
            >
              <i
                className={`ri-facebook-circle-fill text-xl ${form.show_feed ? 'text-blue-600' : 'text-gray-400'}`}
              />
            </div>
            <div>
              <p className="text-sm font-semibold text-brand-dark">{t('admin_facebook')}</p>
              <p className="text-xs text-brand-muted">
                {form.show_feed ? t('admin_visible') : t('admin_hidden')}
              </p>
            </div>
          </div>
          <button
            onClick={() => setForm({ ...form, show_feed: form.show_feed ? 0 : 1 })}
            className={`relative w-12 h-6 rounded-full transition-colors duration-200 cursor-pointer ${form.show_feed ? 'bg-brand-mint' : 'bg-gray-300'}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${form.show_feed ? 'translate-x-6' : ''}`}
            />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-brand-dark mb-1.5">
              {t('admin_facebook_url')}
            </label>
            <input
              type="url"
              value={form.facebook_url}
              onChange={(e) => setForm({ ...form, facebook_url: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
              placeholder={t('admin_placeholder_facebook')}
            />
            <p className="text-xs text-brand-muted mt-1.5">{t('admin_facebook_url')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-dark mb-1.5">
              {t('admin_embed_code')}
            </label>
            <textarea
              rows={5}
              value={form.embed_code}
              onChange={(e) => setForm({ ...form, embed_code: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark font-mono focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors resize-none"
              placeholder={t('admin_embed_placeholder')}
            />
            <p className="text-xs text-brand-muted mt-1.5">{t('admin_embed_hint')}</p>
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
          <p className="text-xs text-brand-muted">{t('admin_backend_desc')}</p>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 shadow-sm"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                {t('admin_saving')}
              </>
            ) : (
              <>
                <i className="ri-save-line" />
                {t('admin_save_changes')}
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-5 flex items-start gap-3">
        <i className="ri-information-line text-blue-600 text-lg mt-0.5 shrink-0" />
        <div>
          <h4 className="text-sm font-semibold text-blue-800">{t('admin_facebook_help')}</h4>
          <p className="text-sm text-blue-700 mt-1">{t('admin_facebook_help_desc')}</p>
        </div>
      </div>
    </div>
  );
}